import {flow, getParent, types} from 'mobx-state-tree';
import DeviceModel from '../models/device-model';
import PositionModel from '../models/position-model';
import SinisterModel from '../models/sinister-model';
import SinisterService from '../services/sinister-service';
import moment from 'moment';
import PositionService from 'src/services/position-service.js';
import {PlatformIntl} from '../infra/PlatformIntl';

/**
 * Lê a data e a hora digitadas no formulário de ocorrência no formato do idioma do navegador (a
 * ordem de dia e mês vem da chave de i18n `dateFormat`, a mesma que o formulário usa para preencher).
 *
 * @param {string} date data digitada
 * @param {string} time hora digitada, HH:mm
 * @return {string} instante em ISO 8601
 */
export const parseOccurrenceDate = (date, time) =>
    moment(`${date} ${time}`, `${PlatformIntl.getInstance().formatMessage('dateFormat')} HH:mm`).toDate().toISOString();

const sinisterService = new SinisterService();

const SinisterStore = types
    .model('SinisterStore', {
        device: types.maybeNull(
            types.reference(types.late(() => DeviceModel)),
        ),
        position: types.maybeNull(
            types.reference(types.late(() => PositionModel)),
        ),
        openingSinister: types.optional(types.boolean, false),
        incidents: types.optional(types.array(SinisterModel), []),
    })
    .views((self) => ({
        get pickOpened() {
            return self.incidents.filter((incident) => incident.status === 'opened');
        },
    }))
    .views((self) => ({
        get list() {
            return self.incidents;
        },
        getOpeningSinister() {
            return self.openingSinister;
        },
        findById(incidentId) {
            return self.incidents.find((incident) => incident.id === incidentId);
        },
    }))
    .actions((self) => ({
        afterCreate() {
            self.fetch();
        },
        setDevice(data) {
            self.device = data;
        },
        setPosition(data) {
            self.position = data;
        },
        setIncidents(data) {
            self.incidents = data;
        },
        openNewSinister() {
            self.openingSinister = true;
        },
        rollbackNewSinister() {
            self.openingSinister = false;
            self.fetch();
        },
        fetch() {
            sinisterService.fetch(true).then((incidents) => self.setIncidents(incidents));
        },
        newSinister(position, device, customer, session) {
            const hasNew = self.incidents.filter((i) => i.id === 0);
            if (hasNew.length === 0) {
                const sinister = {
                    id: 0,
                    number: 0,
                    deviceId: device.id,
                    customerId: device.customerId,
                    devicePlate: device.attributes.hasOwnProperty('plate') ? device.attributes.plate : '',
                    customerName: customer.name,
                    openedBy: session.email,
                    openingAddress: position.address,
                    openingLatitude: position.latitude,
                    openingLongitude: position.longitude,
                    status: 'opened',
                    vehicleDamaged: false,
                    device: device,
                    customer: customer,
                };
                self.incidents.unshift(sinister);
            }
        },
        cancelNewSinister() {
            self.openingSinister = false;
            self.fetch();
        },
        save: flow(function* (payload) {
            const
                {
                    deviceId,
                    customerId,
                    openingType,
                    openingAddress,
                    openingDescription,
                    openingLatitude,
                    openingLongitude,
                } = payload;

            const {devices: deviceStore} = getParent(self);
            const device = deviceStore.findById(payload.deviceId);
            if (device && !device.positionId) {
                const positionService = new PositionService();
                yield positionService.pushOsmAndPosition({
                    deviceUniqueId: device.uniqueId,
                    lat: payload.openingLatitude,
                    lon: payload.openingLongitude,
                });
            }

            const result = yield sinisterService.create({
                deviceId,
                customerId,
                openingDate: new Date().toISOString(),
                openingType,
                openingAddress,
                openingDescription,
                openingLatitude,
                openingLongitude,
            });

            return result;
        }),
    }))
    .actions((self) => ({
        applyPositionAndSave: flow(function* (payload) {
            const {devices: deviceStore} = getParent(self);
            const device = deviceStore.findById(payload.deviceId);

            const positionService = new PositionService();
            yield positionService.pushOsmAndPosition({
                deviceUniqueId: device.uniqueId,
                lat: payload.openingLatitude,
                lon: payload.openingLongitude,
            });

            return self.save(payload);
        }),

        applyPositionAndAddOccurrence: flow(function* (payload) {
            const currentIncident = self.findById(payload.sinisterId);
            const {devices: deviceStore} = getParent(self);
            const device = deviceStore.findById(currentIncident.deviceId);

            const positionService = new PositionService();
            yield positionService.pushOsmAndPosition({
                deviceUniqueId: device.uniqueId,
                lat: payload.latitude,
                lon: payload.longitude,
            });

            const {
                sinisterId,
                category,
                address,
                description,
                ext,
                file,
            } = payload;

            return sinisterService.addOccurrences({
                sinisterId,
                attributes: {ext, file},
                category,
                occurrenceDate: parseOccurrenceDate(payload.date, payload.time),
                address,
                description,
            });
        }),
    }))
    .views((self) => ({
        //
    }));

export default SinisterStore;

